import React from "react";
import { createGlobalStyle } from "styled-components";

import * as Animation from "~/Animation";
import { Asset } from "~/Asset";
import * as Color from "~/Color";
import { Distance } from "~/Distance";
import * as Driver from "~/Driver";
import * as Icon from "~/Icon";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { Order } from "~/Order";
import * as Shadow from "~/Shadow";
import { Table as GenericTable } from "~/TableV2";
import { Time } from "~/Time";
import { Toast } from "~/Toast";
import * as Typography from "~/Typography";
import { injectParing } from ".";
import { NewBranch } from "../../Branch";
import {
  useAllSuggestedPairings,
  SuggestedPairing,
} from "./SuggestedPairingHook";
import { Remote } from "~/Remote";

type UnassignedTableRecord = Log.Unassigned.Unassigned & {
  readonly key: string;
};

export const Component = () => {
  const rowRoute = Log.Unassigned.Route.useRowRoute();
  React.useEffect(() => rowRoute(), []);

  const interval = Log.Unassigned.Route.useRouteInterval();

  const {
    isLoading: paringsAreLoading,
    pairings,
  } = Driver.Pairing.useUnfilteredPairings(interval);

  const {
    isLoading: unassignedLogsAreLoading,
    unassignedLogs,
  } = Log.Unassigned.useWithLoading(interval);

  const drivers = Driver.Drivers.use();
  const driversAreLoading = !drivers || !drivers.drivers;

  const flattenedPairings = React.useMemo(() => {
    if (!paringsAreLoading && pairings) {
      return Object.values(pairings).reduce(
        (previous, current) => [...previous, ...current],
        []
      );
    }
    return undefined;
  }, [pairings, paringsAreLoading]);

  const data = React.useMemo(() => {
    if (
      flattenedPairings &&
      !unassignedLogsAreLoading &&
      !driversAreLoading &&
      unassignedLogs
    ) {
      return unassignedLogs.map((log) => {
        if (!log.driver.id) {
          const newLog = injectParing({
            log,
            pairings: flattenedPairings,
            ...drivers,
          });
          return { key: `${newLog.driver.id}${newLog.id}`, ...newLog };
        }
        return { key: `0${log.id}`, ...log };
      });
    }
    return [];
  }, [flattenedPairings, unassignedLogs, unassignedLogsAreLoading, drivers]);

  // Track pagination state
  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 10,
  });

  // Memoize date strings to ensure they only change when the interval actually changes
  // IMPORTANT: Depend on .dateTime (the primitive timestamp) not the DateTime object itself
  // This ensures the query fetches new data when user picks a new date from the date selector
  const dateRange = React.useMemo(
    () => ({
      startDate: Time.DateTime.toISO(interval.interval.start),
      endDate: Time.DateTime.toISO(interval.interval.stop),
    }),
    [interval.interval.start.dateTime, interval.interval.stop.dateTime]
  );

  // Fetch all suggested pairings for the entire interval range
  // This ensures consistent results regardless of pagination
  const suggestedPairings = useAllSuggestedPairings({
    ...dateRange,
    active: false,
    pageSize: SuggestedPairing.DEFAULT_PAGE_SIZE, // Configurable: number of suggestions to fetch per request
  });

  const onRowClick: React.ComponentProps<typeof GenericTable>["onRow"] = (
    record
  ) => ({
    onClick: () => {
      const { interval, driver } = record as UnassignedTableRecord;
      if (driver && driver?.id && driver?.name) {
        const route = {
          ...interval.start,
          driver: { id: driver.id, name: driver.name },
        };

        // eslint-disable-next-line functional/no-expression-statement
        rowRoute(route);
      }
    },
  });

  const suggestedPairingsAreLoading = Remote.isLoading(suggestedPairings);
  const suggestedPairingsHaveError = Remote.isError(suggestedPairings);

  const isLoading =
    unassignedLogsAreLoading ||
    driversAreLoading ||
    paringsAreLoading ||
    suggestedPairingsAreLoading;

  // Convert Remote to data and error for easier use
  const suggestedPairingsData = Remote.toUndefined(suggestedPairings);

  // Extract error message from Remote
  const suggestedPairingsErrorMessage = Remote.when(suggestedPairings, {
    loading: () => undefined,
    error: (error) => error.message,
    success: () => undefined,
  });

  // State to control toast visibility
  const [showErrorToast, setShowErrorToast] = React.useState(false);

  // Show toast when there's an error
  React.useEffect(() => {
    if (suggestedPairingsHaveError) {
      setShowErrorToast(true);
    } else {
      setShowErrorToast(false);
    }
  }, [suggestedPairingsHaveError, suggestedPairings]);

  const handleTableChange = React.useCallback(
    (newPagination: {
      readonly current: number;
      readonly pageSize: number;
    }) => {
      setPagination({
        current: newPagination.current,
        pageSize: newPagination.pageSize,
      });
    },
    []
  );

  return (
    <>
      {showErrorToast && suggestedPairingsErrorMessage && (
        <Toast
          message="Suggested Pairings Error"
          description={suggestedPairingsErrorMessage}
          type="error"
          onClose={() => setShowErrorToast(false)}
        />
      )}
      <GenericTable
        pagination={{
          showSizeChanger: true,
          position: ["bottomCenter"],
          current: pagination.current,
          pageSize: pagination.pageSize,
        }}
        showSorterTooltip={false}
        loading={isLoading}
        columns={
          Column.list(
            suggestedPairingsData,
            drivers,
            suggestedPairingsHaveError
          ) as never
        }
        dataSource={data as never}
        onRow={onRowClick as never}
        onChange={handleTableChange as never}
        // TODO: ^ Actually type when used in other places
      />
    </>
  );
};

export namespace Column {
  export const list = (
    suggestedPairings?: SuggestedPairing.SuggestionsResponse,
    drivers?: Driver.Drivers,
    suggestedPairingsHaveError?: boolean
  ) =>
    [
      Date.column(),
      Interval.column(),
      PairedAsset.column(),
      AssetBranch.column(),
      PairedDriver.column(
        suggestedPairings,
        drivers,
        suggestedPairingsHaveError
      ),
      TotalDistance.column(),
      Location.column(),
    ] as const;

  namespace Date {
    export const column = () =>
      ({
        title: "Date",
        dataIndex: "dateTime",
        defaultSortOrder: "descend",
        sorter,
        render,
        onCell: () => ({ style: { verticalAlign: "top" } }),
      } as const);

    const sorter = (
      a: Log.Unassigned.Unassigned,
      b: Log.Unassigned.Unassigned
    ) => Time.DateTime.compare(a.interval.start, b.interval.start);

    const render = (_: never, unassigned: Log.Unassigned.Unassigned) => (
      <Time.DateTime
        {...unassigned.interval.start}
        Time={() => null}
        Separator={() => null}
        Date={(dateTime) => (
          <Typography.Component strong>
            {Time.DateTime.toDateOnlyMMMDYYYY(dateTime)}
          </Typography.Component>
        )}
      />
    );
  }

  namespace Interval {
    export const column = () =>
      ({
        title: "Time",
        dataIndex: "interval",
        sorter,
        render,
        onCell: () => ({ style: { verticalAlign: "top" } }),
      } as const);

    const sorter = (
      a: Log.Unassigned.Unassigned,
      b: Log.Unassigned.Unassigned
    ) => {
      const now = Time.DateTime.now();
      return Order.number().compare(
        Time.Interval.Stopped.toDuration(
          Time.Interval.Started.toStopped(now)(a)
        ).duration,
        Time.Interval.Stopped.toDuration(
          Time.Interval.Started.toStopped(now)(b)
        ).duration
      );
    };

    const render = (_: never, unassigned: Log.Unassigned.Unassigned) => (
      <Time.Interval
        {...unassigned}
        Start={DateTime}
        Stop={DateTime}
        Duration={Duration}
      />
    );

    const DateTime: React.Fn<Time.DateTime> = (props) => (
      <Time.DateTime {...props} Date={() => null} Separator={() => null} />
    );

    const Duration: React.Fn<Time.Duration> = (duration) => (
      <Typography.Component small style={{ display: "block" }}>
        {Time.Duration.toEnglish(duration)}
      </Typography.Component>
    );
  }

  namespace PairedAsset {
    export const column = () =>
      ({
        title: "Asset",
        dataIndex: "asset",
        render,
        sorter: Asset.compare,
        onCell: () => ({ style: { verticalAlign: "top" } }),
      } as const);

    const render = (_: never, unassigned: Log.Unassigned.Unassigned) => (
      <Asset.Link {...unassigned} />
    );
  }

  namespace AssetBranch {
    export const column = () =>
      ({
        title: "Branch",
        dataIndex: "branch",
        render,
        sorter: (
          a?: Log.Unassigned.Unassigned,
          b?: Log.Unassigned.Unassigned
        ) => NewBranch.compare(a?.branch, b?.branch),
        onCell: () => ({ style: { verticalAlign: "top" } }),
      } as const);

    const render = (_: never, unassigned: Log.Unassigned.Unassigned) => (
      <NewBranch.Name branch={unassigned.branch} />
    );
  }

  namespace PairedDriver {
    export const column = (
      suggestedPairings?: SuggestedPairing.SuggestionsResponse,
      drivers?: Driver.Drivers,
      hasError?: boolean
    ) =>
      ({
        title: hasError ? "Possible Driver ⚠️" : "Possible Driver",
        dataIndex: "driver",
        render: (_: never, unassigned: Log.Unassigned.Unassigned) =>
          render(unassigned, suggestedPairings, drivers),
        sorter,
        onCell: () => ({ style: { verticalAlign: "top" } }),
      } as const);

    const findMatchingSuggestions = (
      unassigned: Log.Unassigned.Unassigned,
      suggestedPairings?: SuggestedPairing.SuggestionsResponse
    ): readonly SuggestedPairing.Suggestion[] => {
      if (!suggestedPairings || !unassigned.asset.id) {
        return [];
      }

      // Convert log to a stopped interval (using now if ongoing)
      const logInterval = Time.Interval.Stopped.fromInterval(() =>
        Time.DateTime.now()
      )(unassigned);

      return suggestedPairings.suggestions.filter((suggestion) => {
        // Skip suggestions that have been rejected.
        if (suggestion.rejectionDate) {
          return false;
        }

        // Check if asset matches
        if (suggestion.assetId !== parseInt(unassigned.asset.id, 10)) {
          return false;
        }

        // Use recognitionDate as start and expirationDate as end
        // Default to +14 hours from recognition if expiration not set
        const recognitionDateTime = Time.DateTime.fromISOUnsafe(
          suggestion.recognitionDate
        );
        const suggestionInterval = Time.Interval.Stopped.fromDateTimes(
          recognitionDateTime,
          suggestion.expirationDate
            ? Time.DateTime.fromISOUnsafe(suggestion.expirationDate)
            : Time.DateTime.add(recognitionDateTime)(14, "hours")
        );

        // Check if log is completely contained within the suggestion period
        return Time.Interval.Stopped.contained(suggestionInterval)(logInterval);
      });
    };

    const render = (
      unassigned: Log.Unassigned.Unassigned,
      suggestedPairings?: SuggestedPairing.SuggestionsResponse,
      drivers?: Driver.Drivers
    ) => {
      const matchingSuggestions = findMatchingSuggestions(
        unassigned,
        suggestedPairings
      );

      const hasOriginalDriver = !!unassigned.driver && !!unassigned.driver.id;

      if (matchingSuggestions.length > 0 && drivers?.drivers) {
        // Find the suggestion with recognition date closest to the log's start time
        const logStartTime = Time.DateTime.toValue(unassigned.interval.start);

        const closestSuggestion = matchingSuggestions.reduce(
          (closest, current) => {
            const currentStartTime = Time.DateTime.fromISOUnsafe(
              current.recognitionDate
            ).dateTime;
            const closestStartTime = Time.DateTime.fromISOUnsafe(
              closest.recognitionDate
            ).dateTime;

            const currentDiff = Math.abs(logStartTime - currentStartTime);
            const closestDiff = Math.abs(logStartTime - closestStartTime);

            return currentDiff < closestDiff ? current : closest;
          }
        );

        const suggestedDriver = drivers.drivers.find(
          (d: Driver.Driver) => d.id === closestSuggestion.driverId.toString()
        );

        if (suggestedDriver) {
          // If the suggested pairing driver is the same as the unassigned driver, only show the suggested driver
          const suggestedPairingDriverAndUnassignedDriverAreTheSame =
            suggestedDriver.id === unassigned.driver.id;
          return (
            <>
              {hasOriginalDriver &&
                !suggestedPairingDriverAndUnassignedDriverAreTheSame && (
                  <Driver.Name.WithLabel.Component
                    {...unassigned.driver}
                    label="Last paired"
                  />
                )}
              <Driver.Name.WithLabel.Component
                {...suggestedDriver}
                label="Suggested by dash cam"
              />
            </>
          );
        }
      }

      return (
        <Driver.Name.WithLabel.Component
          {...unassigned.driver}
          label={`${hasOriginalDriver ? "Last paired" : ""}`}
        />
      );
    };

    const sorter = (
      a: Log.Unassigned.Unassigned,
      b: Log.Unassigned.Unassigned
    ) => Driver.compare({ id: "zzz", ...a.driver }, { id: "zzz", ...b.driver });
  }

  namespace TotalDistance {
    export const column = () =>
      ({
        title: "Distance",
        dataIndex: "distance",
        sorter,
        render,
        onCell: () => ({ style: { verticalAlign: "top" } }),
      } as const);

    const sorter = Distance.compare;
    const render = (_: never, distance: Distance) => <Distance {...distance} />;
  }

  namespace Location {
    export const column = () =>
      ({
        title: "Location",
        dataIndex: "location",
        sorter,
        render,
        onCell: () => ({ style: { verticalAlign: "top" } }),
      } as const);

    const sorter = (
      a: Log.Unassigned.Unassigned,
      b: Log.Unassigned.Unassigned
    ) =>
      Order.string().compare(a.location.start, b.location.start) ||
      Order.optionally(Order.string().compare)(
        a.location.stop,
        b.location.stop
      );

    const render = (
      _: never,
      { location: { start, stop } }: Log.Unassigned.Unassigned
    ) =>
      start === stop ? (
        <>{start}</>
      ) : (
        <>
          {start}
          <Layout.Inline.Component left right>
            <Icon.ArrowRight />
          </Layout.Inline.Component>
          {stop}
        </>
      );
  }
}

export namespace AntOverride {
  export const Component = createGlobalStyle`
    .ant-table {
      color: inherit;
      font-size: ${Typography.FontSize.data()};
      line-height: 1em;
    }

    td.ant-table-cell {
      background: none;
    }

    .ant-table-row {
      ${() => Animation.Transition.styles(Animation.medium())}
      &:hover {
        ${Shadow.Box.styles(Shadow.medium())};
      }
    }

    .ant-table-thead > tr > th {
      border-bottom: 1px solid ${Color.veryWhiteGrey()};
      font-size: ${Typography.FontSize.base()};
      font-weight: bold;
    }

    .ant-table-thead > tr > th,
    .ant-table-tbody > tr.ant-table-row:hover > td {
      background: ${Color.almostWhite()} !important;
    }

    .ant-table-tbody > tr > td {
      border-bottom: 1px solid ${Color.veryWhiteGrey()};
    }

    .ant-table-column-sorter-up, .ant-table-column-sorter-down {
      color: ${Color.translucentBlack(0.5)};
    }

    .ant-table-column-sorter-up.active, .ant-table-column-sorter-down.active {
      color: ${Color.azure()};
    }

    .ant-pagination li {
      & div.ant-select-item.ant-select-item-option {
        background: none !important;
        .ant-select-item-option-content {
          &,
          &:hover {
            color: ${Color.black()} !important;
          }
        }
      }

      &,
      & * {
        color: ${Color.black()} !important;
        border-color: ${Color.veryWhiteGrey()} !important;
        transition: ${() =>
          Animation.Transition.style(Animation.fast())} !important;
      }

      &.ant-pagination-item-active,
      &:hover,
      &:active {
        &,
        & * {
          color: ${Color.azure()} !important;
          border-color: ${Color.azure()} !important;
          transition: ${() =>
            Animation.Transition.style(Animation.fast())} !important;
        }
      }

      &.ant-pagination-disabled {
        &,
        & *,
        &:hover,
        &:hover *,
        &:active,
        &:active * {
          color: ${Color.veryWhiteGrey()} !important;
          border-color: ${Color.veryWhiteGrey()} !important;
          box-shadow: none;
        }
      }
    }
  `;
}
